import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  Menu,
  X,
  Mail,
  Phone,
  MapPin,
  Clock,
  CheckCircle2,
  Globe,
  ChevronDown,
  Sun,
  Moon,
  LogIn
} from "lucide-react";
import LogoIndak from "../../assets/img/logo-pdf.png";
import ImageCustomerSupportAI from "../../assets/img/customer_support_ai.png";
import { getAutoTheme } from "../../utils/themeHelper";
import "../home/home.css";

const FacebookIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
);

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
);

const TwitterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path></svg>
);

const WhatsAppIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
);

export default function Contact() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");
  const [langOpen, setLangOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState(getAutoTheme);
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;

    // Scroll Reveal Intersection Observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("revealed");
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll(".reveal, .reveal-left, .reveal-right, .reveal-scale").forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [lang]);

  // Sync theme
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("landing-theme", newTheme);
  };

  const changeLanguage = (newLang) => {
    setLang(newLang);
    localStorage.setItem("lang", newLang);
    setLangOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error(lang === "ar" ? "يرجى ملء جميع الحقول" : "Please fill in all fields");
      return;
    }
    setFormSubmitted(true);
    toast.success(lang === "ar" ? "جاري توجيهك إلى واتساب والبريد..." : "Redirecting you to WhatsApp and Mail...");

    const fullMsg = `Name: ${name}\nEmail: ${email}\nMessage: ${message}`;

    window.open(`https://wa.me/6287773243055?text=${encodeURIComponent(fullMsg)}`, "_blank");
    window.location.href = `mailto:info@indak.id?subject=Contact Form - ${encodeURIComponent(name)}&body=${encodeURIComponent(fullMsg)}`;

    setName("");
    setEmail("");
    setMessage("");
    setTimeout(() => setFormSubmitted(false), 5000);
  };

  const text = {
    en: {
      navHome: "Home",
      navAbout: "About",
      navServices: "Services",
      navContact: "Contact",
      navCheckRates: "Check Rates",
      navSignIn: "Sign In",
      navDashboard: "Dashboard",
      badgeContact: "Get In Touch",
      heroTitle: "Contact Us",
      heroSubtitle: "Have questions about customs regulations, large volume cargo rates, or want to partner with us? Reach out directly to our support team.",
      formTitle: "Send Message",
      formName: "Full Name",
      formEmail: "Email Address",
      formMessage: "Your Message",
      formPlaceholderName: "Your Name...",
      formPlaceholderMessage: "Type your query or message here...",
      formBtnSubmit: "Send Message",
      formBtnSuccess: "Sent",
      hubBadge: "Office Location",
      hubTitle: "Our Hub",
      hubAddressTitle: "Office Address",
      hubAddressValue: "Jl. Bango 3 no. 31, Cilandak, Jakarta Selatan, Indonesia",
      hubHoursTitle: "Operational Hours",
      hubHoursValue: "Monday - Friday: 09:00 - 18:00 WIB",
      hubEmailTitle: "Business Relations Email",
      hubPhoneTitle: "Customer Service Phone",
      socialBadge: "Social Networks",
      socialTitle: "Stay Connected",
      socialDesc: "Follow our updates on cargo schedules, exchange rates, and promotions on social media.",
      footerDesc: "Trusted international logistics and remittance solutions from Indonesia to Yemen and Saudi Arabia.",
      footerCopy: "All rights reserved."
    },
    ar: {
      navHome: "الرئيسية",
      navAbout: "من نحن",
      navServices: "خدماتنا",
      navContact: "اتصل بنا",
      navCheckRates: "أسعار الشحن",
      navSignIn: "تسجيل الدخول",
      navDashboard: "لوحة التحكم",
      badgeContact: "تواصل معنا",
      heroTitle: "اتصل بنا",
      heroSubtitle: "هل لديك أسئلة حول لوائح الجمارك، أسعار الشحن للأحجام الكبيرة، أو ترغب في شراكة؟ تواصل مباشرة مع فريق الدعم لدينا.",
      formTitle: "إرسال رسالة",
      formName: "الاسم الكامل",
      formEmail: "البريد الإلكتروني",
      formMessage: "رسالتك",
      formPlaceholderName: "اسمك الكريم...",
      formPlaceholderMessage: "اكتب رسالتك أو استفسارك هنا...",
      formBtnSubmit: "إرسال الرسالة",
      formBtnSuccess: "تم الإرسال",
      hubBadge: "تفاصيل الموقع",
      hubTitle: "مركزنا",
      hubAddressTitle: "عنوان المكتب",
      hubAddressValue: "شارع بانغو ٣ رقم ٣١، سيلانداك، جنوب جاكرتا، إندونيسيا",
      hubHoursTitle: "ساعات العمل",
      hubHoursValue: "الاثنين - الجمعة: 09:00 - 18:00 بتوقيت إندونيسيا",
      hubEmailTitle: "البريد الإلكتروني للعمل",
      hubPhoneTitle: "هاتف خدمة العملاء",
      socialBadge: "شبكات التواصل",
      socialTitle: "ابق على اتصال",
      socialDesc: "تابع آخر تحديثاتنا حول مواعيد الشحن، أسعار الصرف، والعروض عبر وسائل التواصل الاجتماعي.",
      footerDesc: "حلول لوجستية وحوالات مالية دولية موثوقة من إندونيسيا إلى اليمن والمملكة العربية السعودية.",
      footerCopy: "جميع الحقوق محفوظة."
    },
    id: {
      navHome: "Beranda",
      navAbout: "Tentang Kami",
      navServices: "Layanan",
      navContact: "Kontak",
      navCheckRates: "Cek Ongkir",
      navSignIn: "Masuk",
      navDashboard: "Dashboard",
      badgeContact: "Hubungi Kami",
      heroTitle: "Hubungi Kami",
      heroSubtitle: "Punya pertanyaan tentang regulasi bea cukai, tarif kargo volume besar, atau ingin bermitra? Hubungi langsung tim dukungan kami.",
      formTitle: "Kirim Pesan",
      formName: "Nama Lengkap",
      formEmail: "Alamat Email",
      formMessage: "Pesan Anda",
      formPlaceholderName: "Nama Anda...",
      formPlaceholderMessage: "Tulis pertanyaan atau pesan Anda di sini...",
      formBtnSubmit: "Kirim Pesan",
      formBtnSuccess: "Terkirim",
      hubBadge: "Lokasi Kantor",
      hubTitle: "Pusat Kami",
      hubAddressTitle: "Alamat Kantor",
      hubAddressValue: "Jl. Bango 3 no. 31, Cilandak, Jakarta Selatan, Indonesia",
      hubHoursTitle: "Jam Operasional",
      hubHoursValue: "Senin - Jumat: 09:00 - 18:00 WIB",
      hubEmailTitle: "Email Relasi Bisnis",
      hubPhoneTitle: "Telepon Layanan Pelanggan",
      socialBadge: "Jaringan Sosial",
      socialTitle: "Tetap Terhubung",
      socialDesc: "Ikuti pembaruan jadwal kargo, kurs mata uang, dan promo di media sosial kami.",
      footerDesc: "Solusi logistik dan transfer uang internasional terpercaya dari Indonesia ke Yaman dan Arab Saudi.",
      footerCopy: "Hak cipta dilindungi undang-undang."
    }
  };

  const t = text[lang] || text.en;

  return (
    <div className="landing-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* HEADER NAVBAR */}
      <header className="landing-header">
        <div className="header-container">
          <div className="header-logo" onClick={() => navigate("/")}>
            <img src={LogoIndak} alt="Indak Logo" className="logo-img" />
          </div>

          <nav className="desktop-nav">
            <button onClick={() => navigate("/")} className="nav-link">{t.navHome}</button>
            <button onClick={() => navigate("/about")} className="nav-link">{t.navAbout}</button>
            <button onClick={() => navigate("/services")} className="nav-link">{t.navServices}</button>
            <button onClick={() => navigate("/contact")} className="nav-link active-link-highlight">{t.navContact}</button>
            <button onClick={() => navigate("/check-rates")} className="nav-link">{t.navCheckRates}</button>

            {/* Language Switcher */}
            <div className="lang-dropdown-container">
              <button className="lang-dropdown-trigger" onClick={() => setLangOpen(!langOpen)}>
                <Globe size={16} />
                <span>{lang === "en" ? "EN" : lang === "id" ? "ID" : "AR"}</span>
                <ChevronDown size={14} />
              </button>
              {langOpen && (
                <div className="lang-dropdown-menu">
                  <button className={`lang-dropdown-item ${lang === "en" ? "active-lang" : ""}`} onClick={() => changeLanguage("en")}>
                    English
                  </button>
                  <button className={`lang-dropdown-item ${lang === "id" ? "active-lang" : ""}`} onClick={() => changeLanguage("id")}>
                    Bahasa Indonesia
                  </button>
                  <button className={`lang-dropdown-item ${lang === "ar" ? "active-lang" : ""}`} onClick={() => changeLanguage("ar")}>
                    العربية
                  </button>
                </div>
              )}
            </div>

            {/* Theme Toggle Button */}
            <button onClick={toggleTheme} className="theme-toggle-btn" title={theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}>
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </button>

            {/* Login Door Icon Button */}
            <button
              onClick={() => navigate(isLoggedIn ? "/admin/dashboard" : "/login")}
              className="nav-sig-button"
              title={isLoggedIn ? t.navDashboard : t.navSignIn}
            >
              <LogIn size={20} />
            </button>
          </nav>

          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="mobile-nav">
            <button onClick={() => navigate("/")} className="mobile-nav-link">{t.navHome}</button>
            <button onClick={() => navigate("/about")} className="mobile-nav-link">{t.navAbout}</button>
            <button onClick={() => navigate("/services")} className="mobile-nav-link">{t.navServices}</button>
            <button onClick={() => navigate("/contact")} className="mobile-nav-link">{t.navContact}</button>
            <button onClick={() => navigate("/check-rates")} className="mobile-nav-link">{t.navCheckRates}</button>

            <div style={{ display: "flex", gap: "20px", padding: "12px 0", alignItems: "center" }}>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => changeLanguage("en")}
                  style={{ background: "none", border: "none", color: lang === "en" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  EN
                </button>
                <span style={{ color: "#444" }}>|</span>
                <button
                  onClick={() => changeLanguage("id")}
                  style={{ background: "none", border: "none", color: lang === "id" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  ID
                </button>
                <span style={{ color: "#444" }}>|</span>
                <button
                  onClick={() => changeLanguage("ar")}
                  style={{ background: "none", border: "none", color: lang === "ar" ? "#c82333" : "#aaa", fontWeight: "700", cursor: "pointer" }}
                >
                  AR
                </button>
              </div>

              <button
                onClick={toggleTheme}
                style={{ background: "none", border: "none", color: "var(--header-link)", cursor: "pointer", padding: "4px" }}
              >
                {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
              </button>
            </div>

            <button onClick={() => navigate(isLoggedIn ? "/admin/dashboard" : "/login")} className="mobile-nav-sig-cta">
              <LogIn size={18} /> {isLoggedIn ? t.navDashboard : t.navSignIn}
            </button>
          </div>
        )}
      </header>

      {/* CONTACT HERO */}
      <section className="inner-hero-section">
        <div className="section-container">
          <div className="inner-hero-grid-contact">
            <div className="hero-content reveal-left">
              <span className="section-badge">{t.badgeContact}</span>
              <h1 className="hero-title">
                {lang === "ar" ? (
                  <>
                    اتصل <span className="text-gradient">بنا</span>
                  </>
                ) : (
                  <>
                    Contact <span className="text-gradient">Us</span>
                  </>
                )}
              </h1>
              <p className="hero-subtitle">
                {t.heroSubtitle}
              </p>
            </div>
            <div className="subpage-side-visual reveal-right">
              <img src={ImageCustomerSupportAI} alt="Customer Support Visual" />
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT GRID MAP AND FORM (With Motion Reveal) */}
      <section className="contact-main-section">
        <div className="section-container">
          <div className="contact-grid">
            {/* Form */}
            <div className="contact-form-container reveal-left">
              <h3 className="form-card-title">{t.formTitle}</h3>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name">{t.formName}</label>
                  <input
                    type="text"
                    id="name"
                    placeholder={t.formPlaceholderName}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-custom-dark"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">{t.formEmail}</label>
                  <input
                    type="email"
                    id="email"
                    placeholder="name@email.com..."
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-custom-dark"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">{t.formMessage}</label>
                  <textarea
                    id="message"
                    rows="5"
                    placeholder={t.formPlaceholderMessage}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="textarea-custom-dark"
                  ></textarea>
                </div>
                <button type="submit" className="btn-contact-submit" disabled={formSubmitted}>
                  {formSubmitted ? (
                    <span className="submit-success"><CheckCircle2 size={16} /> {t.formBtnSuccess}</span>
                  ) : (
                    t.formBtnSubmit
                  )}
                </button>
              </form>
            </div>

            {/* Hub Info */}
            <div className="contact-info reveal-right">
              <span className="section-badge">{t.hubBadge}</span>
              <h2 className="section-title">{t.hubTitle}</h2>

              <div className="contact-details-list">
                <div className="contact-detail-item">
                  <div className="contact-icon-box"><MapPin size={20} /></div>
                  <div>
                    <h4>{t.hubAddressTitle}</h4>
                    <p>{t.hubAddressValue}</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon-box"><Clock size={20} /></div>
                  <div>
                    <h4>{t.hubHoursTitle}</h4>
                    <p>{t.hubHoursValue}</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon-box"><Mail size={20} /></div>
                  <div>
                    <h4>{t.hubEmailTitle}</h4>
                    <p>info@indak.id</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon-box"><Phone size={20} /></div>
                  <div>
                    <h4>{t.hubPhoneTitle}</h4>
                    <p>+62 877-7324-3055</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="contact-map-wrapper">
                <iframe
                  title="Indak Office Map Contact"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3965.7876771343714!2d106.7997977!3d-6.2916053!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e69f1e1a533bfad%3A0xc6cb1b58f8b3c6dd!2sJl.%20Bango%20III%20No.31%2C%20RT.10%2FRW.3%2C%20Cilandak%20Bar.%2C%20Kec.%20Cilandak%2C%20Kota%20Jakarta%20Selatan%2C%20Daerah%20Khusus%20Ibukota%20Jakarta%2012430!5e0!3m2!1sid!2sid!4v1786262144619!5m2!1sid!2sid"
                  width="100%"
                  height="220px"
                  style={{ border: 0, borderRadius: "14px", marginTop: "24px" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STAY CONNECTED / SOCIALS (With Scale Reveals) */}
      <section className="contact-socials-section">
        <div className="section-container">
          <div className="section-header-center reveal">
            <span className="section-badge">{t.socialBadge}</span>
            <h2 className="section-title">{t.socialTitle}</h2>
            <p className="section-desc-center">
              {t.socialDesc}
            </p>
          </div>

          <div className="social-links-grid">
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="social-card reveal-scale reveal-delay-1">
              <FacebookIcon />
              <span>Facebook</span>
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="social-card reveal-scale reveal-delay-2">
              <InstagramIcon />
              <span>Instagram</span>
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="social-card reveal-scale reveal-delay-3">
              <TwitterIcon />
              <span>Twitter / X</span>
            </a>
            <a href="https://wa.me/6287773243055" target="_blank" rel="noopener noreferrer" className="social-card whatsapp-card reveal-scale reveal-delay-1">
              <WhatsAppIcon />
              <span>WhatsApp Chat</span>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-main-grid">
            <div className="footer-col footer-col-brand">
              <p className="footer-brand-desc">{t.footerDesc}</p>
            </div>

            <div className="footer-col footer-col-links">
              <h4 className="footer-col-title">{lang === "ar" ? "روابط سريعة" : lang === "id" ? "Navigasi Cepat" : "Quick Links"}</h4>
              <ul className="footer-links-list">
                <li><button onClick={() => navigate("/")}>{t.navHome}</button></li>
                <li><button onClick={() => navigate("/about")}>{t.navAbout}</button></li>
                <li><button onClick={() => navigate("/services")}>{t.navServices}</button></li>
                <li><button onClick={() => navigate("/check-rates")}>{t.navCheckRates}</button></li>
                <li><button onClick={() => navigate("/contact")}>{t.navContact}</button></li>
              </ul>
            </div>

            <div className="footer-col footer-col-services">
              <h4 className="footer-col-title">{lang === "ar" ? "وجهات الخدمة" : lang === "id" ? "Cakupan Destinasi" : "Service Coverage"}</h4>
              <ul className="footer-links-list text-muted-list">
                <li>🇾🇪 Yemen (Tarim, Mukalla, Aden, Sana'a)</li>
                <li>🇸🇦 Saudi Arabia (Jeddah, Riyadh, Makkah)</li>
                <li>📦 Air & Sea Cargo Shipping</li>
                <li>💵 Outbound & Inbound Money Transfer</li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="copyright-text">
              &copy; {new Date().getFullYear()} INDAK. {t.footerCopy}
            </p>
            {/* <div className="dev-tag">
              <span className="dev-by">{lang === "ar" ? "تطوير بواسطة" : "Dev By"}</span>
              <span className="dev-name">~ YallaBob ~</span>
            </div> */}
          </div>
        </div>
      </footer>
    </div>
  );
}
